import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Construction, Info, Square, Shapes } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import ShapeEditor, { computeShape } from './ShapeEditor';
import CalculatorSaveBar from '../../components/calculators/CalculatorSaveBar';
import useCalculatorSave from '../../components/calculators/useCalculatorSave';

// ─── Concrete pricing assumptions (editable by user) ─────────────
const DEFAULT_ASSUMPTIONS = {
  // $/yd³
  gravel_cost_per_yd3: 45,
  concrete_cost_per_yd3: 140,
  forms_misc_flat: 25,
  // in — gravel base thickness (in addition to slab thickness for total excavation depth)
  gravel_base_in: 4,
};

// Per-type tuning: recommended slab thickness, excavation assumption, labour multipliers
const TYPE_PROFILES = {
  walkway: {
    label: 'Walkway',
    rec_thickness_in: 4,
    excav_rate_hr_per_yd3: 3.75, // hand-dig moderate soil
    rate_adjust: 1.0,
  },
  patio: {
    label: 'Patio',
    rec_thickness_in: 4,
    excav_rate_hr_per_yd3: 3.5,
    rate_adjust: 1.0,
  },
  driveway: {
    label: 'Driveway',
    rec_thickness_in: 6,
    excav_rate_hr_per_yd3: 3.0,
    rate_adjust: 1.1, // slightly more form/rebar work
  },
  foundation: {
    label: 'Foundation / Footing',
    rec_thickness_in: 8,
    excav_rate_hr_per_yd3: 4.0, // deeper + tighter work
    rate_adjust: 1.25,
  },
};

const fmt = (n, d = 2) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtN = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function ConcreteCalculator() {
  const navigate = useNavigate();
  const [type, setType] = useState('walkway');
  const [shapeMode, setShapeMode] = useState('rectangle'); // 'rectangle' | 'custom'
  const [length_ft, setLength] = useState(8);
  const [width_ft, setWidth] = useState(4);
  const [rectangles, setRectangles] = useState([
    { id: 'r_seed_1', x: 0, y: 0, w: 8, h: 4 },
  ]);
  const [thickness_in, setThickness] = useState(TYPE_PROFILES.walkway.rec_thickness_in);
  const [hourly_rate, setHourlyRate] = useState(65);
  const [markup_pct, setMarkup] = useState(20);
  const [notes, setNotes] = useState('');
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);

  // ── Save lifecycle (Price Book save hook) ──────────────────────
  const getConfig = useCallback(() => ({
    type, shapeMode, length_ft, width_ft, rectangles, thickness_in,
    hourly_rate, markup_pct, notes, assumptions,
  }), [type, shapeMode, length_ft, width_ft, rectangles, thickness_in,
       hourly_rate, markup_pct, notes, assumptions]);
  const applyConfig = useCallback((cfg = {}) => {
    if (cfg.type !== undefined) setType(cfg.type);
    if (cfg.shapeMode !== undefined) setShapeMode(cfg.shapeMode);
    if (cfg.length_ft !== undefined) setLength(cfg.length_ft);
    if (cfg.width_ft !== undefined) setWidth(cfg.width_ft);
    if (cfg.rectangles !== undefined) setRectangles(cfg.rectangles);
    if (cfg.thickness_in !== undefined) setThickness(cfg.thickness_in);
    if (cfg.hourly_rate !== undefined) setHourlyRate(cfg.hourly_rate);
    if (cfg.markup_pct !== undefined) setMarkup(cfg.markup_pct);
    if (cfg.notes !== undefined) setNotes(cfg.notes);
    if (cfg.assumptions !== undefined) setAssumptions(cfg.assumptions);
  }, []);

  // When type changes, suggest the recommended thickness (user can still override)
  useEffect(() => {
    setThickness(TYPE_PROFILES[type].rec_thickness_in);
  }, [type]);

  // Pull user's default hourly rate from job-cost settings
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/job-costs/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s && s.default_hourly_rate) setHourlyRate(s.default_hourly_rate); })
      .catch(() => {});
  }, []);

  const calc = useMemo(() => {
    const profile = TYPE_PROFILES[type];
    // Source area + perimeter from either simple rectangle inputs or the
    // custom shape editor (which supports overlapping rectangles).
    let area_ft2, perimeter_ft;
    if (shapeMode === 'custom') {
      const s = computeShape(rectangles);
      area_ft2 = s.area_ft2;
      perimeter_ft = s.perimeter_ft;
    } else {
      area_ft2 = length_ft * width_ft;
      perimeter_ft = 2 * (length_ft + width_ft);
    }
    const slab_ft = thickness_in / 12;
    const gravel_ft = assumptions.gravel_base_in / 12;
    const excav_depth_ft = slab_ft + gravel_ft;

    // Volumes (yd³ = 27 ft³)
    const excav_yd3 = (area_ft2 * excav_depth_ft) / 27;
    const concrete_yd3 = (area_ft2 * slab_ft) / 27;
    const gravel_yd3 = (area_ft2 * gravel_ft) / 27;

    // Labour
    const excav_hours = excav_yd3 * profile.excav_rate_hr_per_yd3;
    const gravel_hours = Math.max(1, gravel_yd3 * 2.5); // delivery + compact
    // Forming scales with the perimeter (each linear foot of form takes ~3 min to lay)
    const forming_hours = Math.max(1, perimeter_ft * 0.05);
    const pour_hours = Math.max(2, concrete_yd3 * 4); // pour + screed
    const finish_hours = Math.max(1.5, area_ft2 * 0.04); // finish + cleanup per ft²
    const total_hours = (excav_hours + gravel_hours + forming_hours + pour_hours + finish_hours) * profile.rate_adjust;
    const labour_cost = total_hours * hourly_rate;

    // Materials
    const gravel_cost = gravel_yd3 * assumptions.gravel_cost_per_yd3;
    const concrete_cost = concrete_yd3 * assumptions.concrete_cost_per_yd3;
    const forms_cost = assumptions.forms_misc_flat;
    const materials_subtotal = gravel_cost + concrete_cost + forms_cost;

    const labour_markup = labour_cost * (markup_pct / 100);
    const materials_markup = materials_subtotal * (markup_pct / 100);
    const total_markup = labour_markup + materials_markup;
    const total_quote = labour_cost + materials_subtotal + total_markup;

    // Rollup per ft² for pricing intuition
    const price_per_ft2 = area_ft2 > 0 ? total_quote / area_ft2 : 0;

    return {
      area_ft2, perimeter_ft, excav_yd3, concrete_yd3, gravel_yd3,
      labour_breakdown: [
        { label: `Excavation (~${(excav_depth_ft * 12).toFixed(0)}" total depth)`, hours: excav_hours },
        { label: 'Gravel delivery & compact', hours: gravel_hours },
        { label: 'Forming & setup', hours: forming_hours },
        { label: 'Pour & screed concrete', hours: pour_hours },
        { label: 'Finish & cleanup', hours: finish_hours },
      ],
      total_hours, labour_cost,
      materials_breakdown: [
        { label: `Gravel (~$${assumptions.gravel_cost_per_yd3}/yd³ delivered)`, cost: gravel_cost, qty: `${gravel_yd3.toFixed(2)} yd³` },
        { label: `Concrete (~$${assumptions.concrete_cost_per_yd3}/yd³ ready-mix)`, cost: concrete_cost, qty: `${concrete_yd3.toFixed(2)} yd³` },
        { label: 'Forms / stakes / misc.', cost: forms_cost, qty: 'flat' },
      ],
      materials_subtotal, labour_markup, materials_markup, total_markup, total_quote,
      price_per_ft2,
    };
  }, [type, shapeMode, rectangles, length_ft, width_ft, thickness_in, hourly_rate, markup_pct, assumptions]);

  const buildSummary = useCallback(() => {
    const dimLine = shapeMode === 'custom'
      ? `${rectangles.length} rectangle${rectangles.length !== 1 ? 's' : ''} combined, ${calc.perimeter_ft.toFixed(1)} ft outer perimeter, ${thickness_in}" thick`
      : `${length_ft}' × ${width_ft}' × ${thickness_in}"`;
    const text = [
      `${TYPE_PROFILES[type].label} — ${dimLine}`,
      `Area: ${calc.area_ft2.toFixed(2)} ft²  |  Concrete: ${calc.concrete_yd3.toFixed(2)} yd³`,
      `Total: ${fmt(calc.total_quote)} (${fmt(calc.price_per_ft2)}/ft²)`,
      notes ? `Notes: ${notes}` : '',
    ].filter(Boolean).join('\n');
    return {
      text,
      totals: {
        total_quote: calc.total_quote,
        area_ft2: calc.area_ft2,
        price_per_ft2: calc.price_per_ft2,
        labour_cost: calc.labour_cost,
        materials_subtotal: calc.materials_subtotal,
        markup_pct,
      },
    };
  }, [type, shapeMode, rectangles, length_ft, width_ft, thickness_in,
       notes, calc, markup_pct]);

  const saveApi = useCalculatorSave({
    calculatorType: 'concrete',
    getConfig,
    applyConfig,
    getSummary: buildSummary,
  });

  const copySummary = async () => {
    const dimLine = shapeMode === 'custom'
      ? `${rectangles.length} rectangle${rectangles.length !== 1 ? 's' : ''} combined, ${calc.perimeter_ft.toFixed(1)} ft outer perimeter, ${thickness_in}" thick`
      : `${length_ft}' × ${width_ft}' × ${thickness_in}"`;
    const lines = [
      `${TYPE_PROFILES[type].label} — ${dimLine}`,
      `Area: ${calc.area_ft2.toFixed(2)} ft²  |  Concrete: ${calc.concrete_yd3.toFixed(2)} yd³`,
      '',
      'Labour:',
      ...calc.labour_breakdown.map(l => `  ${l.label}: ${l.hours.toFixed(1)} hrs`),
      `  Total: ${calc.total_hours.toFixed(1)} hrs × ${fmt(hourly_rate)} = ${fmt(calc.labour_cost)}`,
      '',
      'Materials:',
      ...calc.materials_breakdown.map(m => `  ${m.label}: ${fmt(m.cost)}`),
      `  Subtotal: ${fmt(calc.materials_subtotal)}`,
      '',
      `Markup (${markup_pct}%): ${fmt(calc.total_markup)}`,
      `TOTAL: ${fmt(calc.total_quote)} (${fmt(calc.price_per_ft2)} / ft²)`,
      notes ? `\nNotes: ${notes}` : '',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      toast.success('Estimate summary copied to clipboard');
    } catch { toast.error('Clipboard unavailable'); }
  };

  return (
    <div className="animate-fade-in" data-testid="concrete-calculator">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/price-book')}
            className="text-[#6B7A73] hover:bg-[#F0F3F1] -ml-2"
            data-testid="concrete-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Construction className="w-5 h-5 text-[#2D6B68]" />
              <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                Concrete Calculator
              </h1>
            </div>
            <p className="text-xs text-[#6B7A73] mt-0.5">
              Slab / flatwork pricing aid — quick quote for walkways, driveways, patios, or footings
            </p>
          </div>
        </div>
        <Button
          onClick={copySummary}
          variant="outline"
          className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md"
          data-testid="copy-summary-btn"
        >
          <Copy className="w-4 h-4 mr-2" /> Copy Summary
        </Button>
      </div>

      <CalculatorSaveBar {...saveApi} />

      {/* Shape picker — spans the full width so the custom editor has room */}
      <div className="bg-white border border-[#E6EBE8] rounded-md p-5 mb-6 space-y-4" data-testid="shape-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Shape & Thickness</h2>
          </div>
          <div className="inline-flex rounded-md border border-[#E6EBE8] bg-white overflow-hidden">
            <button
              onClick={() => setShapeMode('rectangle')}
              data-testid="shape-mode-rectangle"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                shapeMode === 'rectangle' ? 'bg-[#2D6B68] text-white' : 'text-[#1A1D1C] hover:bg-[#F0F3F1]'
              }`}
            >
              <Square className="w-3.5 h-3.5" /> Simple rectangle
            </button>
            <button
              onClick={() => setShapeMode('custom')}
              data-testid="shape-mode-custom"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                shapeMode === 'custom' ? 'bg-[#2D6B68] text-white' : 'text-[#1A1D1C] hover:bg-[#F0F3F1]'
              }`}
            >
              <Shapes className="w-3.5 h-3.5" /> Draw shape
            </button>
          </div>
        </div>

        {shapeMode === 'rectangle' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SliderField label="Length (ft)" value={length_ft} onChange={setLength} min={1} max={60} step={0.5} testid="length-input" />
            <SliderField label="Width (ft)" value={width_ft} onChange={setWidth} min={1} max={30} step={0.5} testid="width-input" />
            <SliderField label="Thickness (in)" value={thickness_in} onChange={setThickness} min={3} max={12} step={0.5} testid="thickness-input" />
          </div>
        ) : (
          <div className="space-y-4">
            <ShapeEditor rectangles={rectangles} onChange={setRectangles} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#E6EBE8]">
              <SliderField label="Thickness (in)" value={thickness_in} onChange={setThickness} min={3} max={12} step={0.5} testid="thickness-input-custom" />
              <div className="flex items-center text-xs text-[#6B7A73] bg-[#F9F9F8] border border-[#E6EBE8] rounded-md p-2.5">
                <span>
                  Slab thickness applies to the entire shape. Recommended: <strong>{TYPE_PROFILES[type].rec_thickness_in}"</strong> for {TYPE_PROFILES[type].label.toLowerCase()}.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — parameters */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5 space-y-4">
            <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Pricing Parameters</h2>

            <div>
              <label className="text-xs text-[#6B7A73] block mb-1">Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="bg-white border-[#E6EBE8]" data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_PROFILES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6B7A73] mt-1">
                Recommends {TYPE_PROFILES[type].rec_thickness_in}" slab thickness.
              </p>
            </div>

            <SliderField label="Hourly rate ($)" value={hourly_rate} onChange={setHourlyRate} min={25} max={200} step={5} testid="hourly-rate-input" prefix="$" />
            <SliderField label="Markup (%)" value={markup_pct} onChange={setMarkup} min={0} max={50} step={1} testid="markup-input" suffix="%" />
          </div>

          {/* Assumptions */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-[#6B7A73]" />
              <h2 className="text-xs font-semibold text-[#6B7A73] uppercase tracking-[0.15em]">Material Prices (editable)</h2>
            </div>
            <SmallInput label="Gravel ($/yd³)" value={assumptions.gravel_cost_per_yd3}
              onChange={v => setAssumptions(a => ({ ...a, gravel_cost_per_yd3: parseFloat(v) || 0 }))} prefix="$" testid="gravel-cost-input" />
            <SmallInput label="Concrete ($/yd³)" value={assumptions.concrete_cost_per_yd3}
              onChange={v => setAssumptions(a => ({ ...a, concrete_cost_per_yd3: parseFloat(v) || 0 }))} prefix="$" testid="concrete-cost-input" />
            <SmallInput label="Forms / stakes (flat $)" value={assumptions.forms_misc_flat}
              onChange={v => setAssumptions(a => ({ ...a, forms_misc_flat: parseFloat(v) || 0 }))} prefix="$" testid="forms-cost-input" />
            <SmallInput label="Gravel base (in)" value={assumptions.gravel_base_in}
              onChange={v => setAssumptions(a => ({ ...a, gravel_base_in: parseFloat(v) || 0 }))} testid="gravel-base-input" />
          </div>

          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <label className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em] block mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Site access, soil conditions, special finish..."
              className="w-full border border-[#E6EBE8] rounded-md p-2 text-sm resize-y min-h-[72px]"
              data-testid="calc-notes-input"
            />
          </div>
        </div>

        {/* RIGHT — results */}
        <div className="lg:col-span-3 space-y-6">
          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-3">
            <MiniCard label="Area" value={`${calc.area_ft2.toFixed(2)} ft²`} />
            <MiniCard label="Excavation" value={`${calc.excav_yd3.toFixed(2)} yd³`} />
            <MiniCard label="Concrete" value={`${calc.concrete_yd3.toFixed(2)} yd³`} />
          </div>

          {/* Labour */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Labour Estimate</h2>
            <div className="space-y-1.5 text-sm">
              {calc.labour_breakdown.map((l, i) => (
                <div key={i} className="flex justify-between" data-testid={`labour-row-${i}`}>
                  <span className="text-[#6B7A73]">{l.label}</span>
                  <span className="text-[#1A1D1C] tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {l.hours.toFixed(1)} hrs
                  </span>
                </div>
              ))}
              <div className="h-px bg-[#E6EBE8] my-2" />
              <div className="flex justify-between font-medium">
                <span className="text-[#1A1D1C]">Total hours</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{calc.total_hours.toFixed(1)} hrs</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">Labour cost (@ {fmt(hourly_rate, 0)})</span>
                <span className="font-semibold text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {fmt(calc.labour_cost)}
                </span>
              </div>
            </div>
          </div>

          {/* Materials */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Materials (before markup)</h2>
            <div className="space-y-1.5 text-sm">
              {calc.materials_breakdown.map((m, i) => (
                <div key={i} className="flex justify-between" data-testid={`material-row-${i}`}>
                  <div>
                    <p className="text-[#6B7A73]">{m.label}</p>
                    <p className="text-[10px] text-[#6B7A73]">{m.qty}</p>
                  </div>
                  <span className="text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {fmt(m.cost)}
                  </span>
                </div>
              ))}
              <div className="h-px bg-[#E6EBE8] my-2" />
              <div className="flex justify-between font-medium">
                <span className="text-[#1A1D1C]">Materials subtotal</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.materials_subtotal)}</span>
              </div>
            </div>
          </div>

          {/* Total quote */}
          <div className="bg-[#F9FAFA] border border-[#2D6B68]/30 rounded-md p-5">
            <h2 className="text-xs font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Total Quote to Customer</h2>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">Labour</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.labour_cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">Materials</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.materials_subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7A73]">Markup ({markup_pct}%)</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(calc.total_markup)}</span>
              </div>
              <div className="h-px bg-[#2D6B68]/30 my-2" />
              <div className="flex justify-between items-baseline">
                <span className="text-base font-bold text-[#1A1D1C]">Total</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#2D6B68]" style={{ fontFamily: "'IBM Plex Mono', monospace" }} data-testid="total-quote">
                    {fmt(calc.total_quote)}
                  </p>
                  <p className="text-[10px] text-[#6B7A73]">
                    {fmt(calc.price_per_ft2)} / ft²
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-[#6B7A73] mt-6 text-center">
        Calculator is a pricing aid only — site conditions, weather, and finish quality will affect real costs.
      </p>
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, step, testid, prefix = '', suffix = '' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs text-[#6B7A73]">{label}</label>
        <Input
          type="number"
          step={step}
          value={value}
          onChange={e => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(v);
          }}
          className="h-7 w-24 text-sm text-right border-[#E6EBE8] text-[#1A1D1C]"
          data-testid={testid}
        />
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
      />
      <div className="flex justify-between mt-0.5">
        <span className="text-[10px] text-[#6B7A73]">{prefix}{min}{suffix}</span>
        <span className="text-[10px] text-[#6B7A73]">{prefix}{max}{suffix}</span>
      </div>
    </div>
  );
}

function SmallInput({ label, value, onChange, prefix = '', testid }) {
  return (
    <div className="flex items-center justify-between">
      <label className="text-xs text-[#6B7A73]">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#6B7A73]">{prefix}</span>}
        <Input
          type="number"
          step="0.01"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`h-7 w-28 text-sm text-right border-[#E6EBE8] ${prefix ? 'pl-6' : ''}`}
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function MiniCard({ label, value }) {
  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">{label}</p>
      <p className="text-lg font-bold text-[#1A1D1C] mt-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {value}
      </p>
    </div>
  );
}
